import {ChangeDetectionStrategy, Component, inject} from "@angular/core";
import {Page} from "../../../navigation/page";
import {PaymentModel} from "../../payment.model";
import {LoadingService} from "../../../ui-elements/loading/loading.service";
import {WalletPaymentSubmissionFailure, WalletPaymentSubmissionResult, WalletProvider} from '../../payment';
import {OrderTotalsPanelComponent} from '../../../order/order-totals-panel/order-totals-panel.component';
import {OrderModel} from '../../../order/order.model';
import {ModalPageLayoutComponent} from '../../../page-layout/modal-page-layout/modal-page-layout.component';
import {ApplePayButtonComponent} from "../apple-pay-button/apple-pay-button.component";
import {GooglePayButtonComponent} from "../google-pay-button/google-pay-button.component";
import {PageFooterControlDirective} from '../../../page-layout/page-footer-control.directive';

/**
 * Used as a fallback to display an "active" wallet payment button in a popup page if the wallet payment provider
 * cannot submit directly via the "passive" button on the payment page or quick-pay panel.
 * Also applies if we don't have a transient user activation (which would have or has caused the wallet payment to fail).
 */
@Component({
    selector: "woa-wallet-payment-page",
    templateUrl: "./wallet-payment-page.component.html",
    styleUrl: "./wallet-payment-page.component.scss",
    providers: [{provide: Page, useExisting: WalletPaymentPageComponent}],
    imports: [
        ApplePayButtonComponent,
        GooglePayButtonComponent,
        ModalPageLayoutComponent,
        OrderTotalsPanelComponent,
        PageFooterControlDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class WalletPaymentPageComponent extends Page {
    public paymentModel: PaymentModel = inject(PaymentModel);
    private loadingService: LoadingService = inject(LoadingService);
    private orderModel: OrderModel = inject(OrderModel);

    WalletProvider = WalletProvider;

    override onActive(): void {
        // This page is created during ordering flow when the spinner is already active. Temporarily suspend the spinner.
        this.loadingService.dismissSpinner();
    }

    /**
     * Called when the wallet payment button is in passive mode (non-tyro, or tyro with direct submission enabled).
     * This is for the case when a transient user activation was not present, requiring a new user interaction to avoid an error when attempting to submit the payment.
     */
    async pay(): Promise<void> {
        // Restart spinner - it will be dismissed when order placement completes.
        await this.loadingService.startSpinner(60000);
        try {
            void this.paymentComplete(await this.paymentModel.walletPaymentProcessor().submit(this.orderModel.orderTotalPayment()));
        } catch (error) {
            void this.paymentFailed(error);
        }
    }

    /**
     * Called when the wallet payment button is in active mode (tyro without direct submission enabled).
     * The payment is submitted by the active button itself.
     */
    async paymentStarted(): Promise<void> {
        // Restart spinner - it will be dismissed when order placement completes.
        await this.loadingService.startSpinner(60000);
    }

    async paymentComplete(walletPaymentResponse: WalletPaymentSubmissionResult): Promise<void> {
        void this.dismiss(walletPaymentResponse);
    }

    async paymentFailed(walletSubmissionFailure: WalletPaymentSubmissionFailure): Promise<void> {
        void this.dismiss(walletSubmissionFailure, true);
    }

    async paymentCancelled(): Promise<void> {
        void this.dismiss();
    }
}
